import React, { useState } from 'react';
import api from '../config/api';

function ImageImportModal({ inventory, onClose, onImport }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [extractedItems, setExtractedItems] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState('');

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('Image file must be smaller than 10MB');
        return;
      }

      setSelectedFile(file);
      setError('');

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleExtractItems = async () => {
    if (!selectedFile) return;

    setIsProcessing(true);
    setError('');

    try {
      console.log('🖼️ Starting image extraction for inventory:', inventory.name);
      console.log('📄 Inventory fields:', inventory.fields);
      console.log('📍 Inventory locations:', inventory.locations);
      
      const formData = new FormData();
      formData.append('image', selectedFile);
      formData.append('inventory_id', inventory.id);

      const response = await api.post('/extract-items', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('✅ AI extraction response:', response.data);
      console.log('📦 Extracted items:', response.data.items);
      setExtractedItems(response.data.items || []);
      setIsValidating(true);
    } catch (error) {
      console.error('Error extracting items:', error);
      setError(error.response?.data?.error || 'Failed to extract items from image');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFieldChange = (itemIndex, fieldName, value) => {
    console.log('🔄 Field changed:', { itemIndex, fieldName, value, type: typeof value });
    setExtractedItems(items => 
      items.map((item, index) => 
        index === itemIndex 
          ? { ...item, field_values: { ...item.field_values, [fieldName]: value } }
          : item
      )
    );
  };

  const handleRemoveItem = (itemIndex) => {
    setExtractedItems(items => items.filter((_, index) => index !== itemIndex));
  };

  const handleImportItems = () => {
    console.log('📤 Importing items:', extractedItems);
    extractedItems.forEach((item, index) => {
      console.log(`Item ${index + 1}:`, item.field_values);
    });
    onImport(extractedItems);
  };

  const renderFieldInput = (item, itemIndex, field) => {
    const value = item.field_values[field.name] || '';

    switch (field.type) {
      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleFieldChange(itemIndex, field.name, e.target.value)}
            style={{ width: '100%', padding: '0.25rem' }}
          >
            <option value="">-- Select --</option>
            {field.options && field.options.map((option, index) => (
              <option key={index} value={option}>{option}</option>
            ))}
          </select>
        );
      case 'radio':
        return (
          <div>
            {field.options && field.options.map((option, index) => (
              <label key={index} style={{ display: 'block', fontSize: '0.9rem' }}>
                <input
                  type="radio"
                  name={`${itemIndex}_${field.name}`}
                  value={option}
                  checked={value === option}
                  onChange={(e) => handleFieldChange(itemIndex, field.name, e.target.value)}
                  style={{ marginRight: '0.5rem' }}
                />
                {option}
              </label>
            ))}
          </div>
        );
      case 'checkbox':
        const checkboxValues = Array.isArray(value) ? value : [];
        return (
          <div>
            {field.options && field.options.map((option, index) => (
              <label key={index} style={{ display: 'block', fontSize: '0.9rem' }}>
                <input
                  type="checkbox"
                  value={option}
                  checked={checkboxValues.includes(option)}
                  onChange={(e) => {
                    const newValues = e.target.checked
                      ? [...checkboxValues, option]
                      : checkboxValues.filter(v => v !== option);
                    handleFieldChange(itemIndex, field.name, newValues);
                  }}
                  style={{ marginRight: '0.5rem' }}
                />
                {option}
              </label>
            ))}
          </div>
        );
      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleFieldChange(itemIndex, field.name, e.target.value)}
            rows="2"
            style={{ width: '100%', padding: '0.25rem' }}
          />
        );
      default:
        return (
          <input
            type={field.type || 'text'}
            value={value}
            onChange={(e) => handleFieldChange(itemIndex, field.name, e.target.value)}
            style={{ width: '100%', padding: '0.25rem' }}
          />
        );
    }
  };

  return (
    <div className="modal">
      <div className="modal-content" style={{ maxWidth: '90vw', width: '1000px' }}>
        <h2>Import Items from Image</h2>
        
        {!isValidating ? (
          <div>
            <div className="form-group">
              <label>Select Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                style={{ marginBottom: '1rem' }}
              />
              <p style={{ fontSize: '0.9rem', color: '#666' }}>
                Upload an image containing a list of items (invoice, catalog, handwritten list, etc.)
              </p>
            </div>

            {imagePreview && (
              <div className="form-group">
                <label>Image Preview</label>
                <div style={{ 
                  border: '1px solid #ddd', 
                  borderRadius: '4px', 
                  padding: '1rem',
                  textAlign: 'center',
                  maxHeight: '400px',
                  overflow: 'auto'
                }}>
                  <img 
                    src={imagePreview} 
                    alt="Upload preview" 
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: '350px',
                      objectFit: 'contain'
                    }} 
                  />
                </div>
              </div>
            )}

            {error && (
              <div style={{ 
                color: '#e74c3c', 
                backgroundColor: '#fdf2f2', 
                padding: '1rem', 
                borderRadius: '4px',
                marginBottom: '1rem'
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
              <button type="button" className="btn" onClick={onClose}>
                Cancel
              </button>
              <button 
                className="btn" 
                onClick={handleExtractItems}
                disabled={!selectedFile || isProcessing}
              >
                {isProcessing ? 'Processing Image...' : 'Extract Items'}
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: '2rem' }}>
              <h3>Extracted Items ({extractedItems.length})</h3>
              <p style={{ color: '#666' }}>
                Review and modify the extracted items before importing. Remove any unwanted items.
              </p>
            </div>

            {extractedItems.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '2rem', 
                color: '#666',
                backgroundColor: '#f9f9f9',
                borderRadius: '4px'
              }}>
                <p>No items were extracted from the image.</p>
                <button 
                  className="btn" 
                  onClick={() => setIsValidating(false)}
                  style={{ marginTop: '1rem' }}
                >
                  Try Another Image
                </button>
              </div>
            ) : (
              <div>
                <div style={{ 
                  maxHeight: '500px', 
                  overflowY: 'auto',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}>
                  {extractedItems.map((item, itemIndex) => (
                    <div key={itemIndex} style={{ 
                      border: '1px solid #eee', 
                      margin: '0.5rem',
                      padding: '1rem',
                      borderRadius: '4px',
                      backgroundColor: '#fafafa'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                        <h4>Item {itemIndex + 1}</h4>
                        <button 
                          className="btn btn-danger" 
                          onClick={() => handleRemoveItem(itemIndex)}
                          style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                        >
                          Remove
                        </button>
                      </div>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                        {inventory.fields.map((field, fieldIndex) => (
                          <div key={fieldIndex}>
                            <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>
                              {field.name}
                              {field.required && <span style={{ color: '#e74c3c' }}> *</span>}
                            </label>
                            {renderFieldInput(item, itemIndex, field)}
                          </div>
                        ))}
                        
                        {inventory.locations && inventory.locations.length > 0 && (
                          <div>
                            <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>
                              Location <span style={{ color: '#e74c3c' }}> *</span>
                            </label>
                            <select
                              value={item.field_values.Location || ''}
                              onChange={(e) => handleFieldChange(itemIndex, 'Location', e.target.value)}
                              style={{ width: '100%', padding: '0.25rem' }}
                            >
                              <option value="">-- Select location --</option>
                              {inventory.locations.map((location, index) => (
                                <option key={index} value={location}>{location}</option>
                              ))}
                              <option value="unknown">unknown</option>
                            </select>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2rem' }}>
                  <button 
                    className="btn" 
                    onClick={() => setIsValidating(false)}
                  >
                    ← Back to Upload
                  </button>
                  <div>
                    <button type="button" className="btn" onClick={onClose} style={{ marginRight: '1rem' }}>
                      Cancel
                    </button>
                    <button 
                      className="btn" 
                      onClick={handleImportItems}
                      disabled={extractedItems.length === 0}
                    >
                      Import {extractedItems.length} Items
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ImageImportModal;